/**
 * Main application entry piont.
 * This function does application initialization and root components rendering.
 */
import 'utils/framework/Logger';
import setupCertona from 'services/Certona';
import setupExchangeRate from 'services/exchangeRates';
import * as TestTarget from 'services/TestTarget';
import * as productInfo from 'services/productInfo';
import * as constructorRecs from 'services/ConstructorRecs';
import * as ConstructorBeacon from 'services/ConstructorBeacon';
import Application from 'utils/framework/Application';
import { Immediate, EventType } from 'constants/events';
import Performance from 'utils/framework/performance/Performance';
import cookieUtils from 'utils/Cookies';
import Store from 'store/Store';
import History from 'services/History';
import CookieActions from 'actions/CookieActions';
import InflateSPA from 'utils/framework/InflateSPA';

// Analytics - This needs to run before any controllers do, so that the promise exists ahead of time
/**
 * Wait for our initial page load tag to have fired before firing any subsequent tags.
 * All subsequent async page load tags depend on the vars set in this tag.
 * Resolve: Called from Signal tag "Global :: Page-load Tracking", Additional Configuration
 */
Sephora.analytics.promises.initialPageLoadFired = new Promise(resolve => {
    Sephora.analytics.resolvePromises.initialPageLoadFired = resolve;
});

import 'utils/framework/InflateComponents';

const runApplication = () => {
    runPreContentLoadInitialization();

    // make sure DOM is ready
    if (Sephora.DOMContentLoadedFired) {
        renderApplication();
    } else {
        window.addEventListener('DOMContentLoaded', renderApplication);
    }

    runServicesInitialization();
};

/**
 * App initialization step #1.
 *
 * Any pre-initialization code should be placed in here.
 */
const runPreContentLoadInitialization = () => {
    // The Immediate event fires once all priority code is loaded but prior to any third party or api dependencies.
    Application.events.dispatchServiceEvent(Immediate, EventType.Ready);
    Application.events.dispatchServiceEvent(Immediate, EventType.ServiceCtrlrsApplied, true);

    Performance.initialize();
};

/**
 * App initialization step #2.
 */
const runPostContentLoadInitialization = () => {
    cookieUtils.initialize();
    Store.initialize();
};

/**
 * Render root components
 */
const renderApplication = async () => {
    try {
        runPostContentLoadInitialization();
        await InflateSPA.renderRootComponents();
        runPostHydrationInitialization();
    } catch (error) {
        if (error) {
            Sephora.logger.error(JSON.stringify(error));
        }
    }
};

/**
 * App initialization step #3.
 *
 * To avoid store mutations before hydration process has finished it's work we need to do all store updates only
 * after first render circle because it's mandatory to render same HTML markup as it was after SSR.
 * Do all initialization store mutations from here because this function is invoked right after hydration.
 */
const runPostHydrationInitialization = () => {
    Store.onHydrationFinished();

    History.initFrontEndRouter();

    Store.dispatch(CookieActions.setAllCookies(Sephora.Util.InflatorComps.pageLoadCookies));
};

/**
 * App initialization step #4.
 *
 * It may run before initialization step #2 (`runPostContentLoadInitialization`) when `DOMContentLoaded` event has not fired yet.
 * Initialize the third-party and internal services.
 * These are listed in order of priority.
 * Generally speaking each of these hooks up to any third party JS files needed and or requests expected data from the third party service.
 */
const runServicesInitialization = () => {
    import(/* webpackChunkName: "components" */ 'services/UserInfo');
    import(/* webpackChunkName: "components" */ 'services/P13N');
    import(/* webpackChunkName: "components" */ 'services/LithiumHeaderImage');
    import(/* webpackChunkName: "components" */ 'services/Search');
    import(/* webpackChunkName: "components" */ 'services/Catalog');
    import(/* webpackChunkName: "components" */ 'services/RefreshBasket');

    setupCertona();
    setupExchangeRate();
    TestTarget.initialize();
    productInfo.initialize();
    constructorRecs.initialize();
    ConstructorBeacon.initializeConstructorBeacon();

    // require.ensure is used to package nested requires in components.chunk.js
    // rather than priority.bundle.js in order to keep priority.bundle.js lean
    import(/* webpackChunkName: "components" */ 'services/Location');
    import(/* webpackChunkName: "components" */ 'services/OrderInfo');
    import(/* webpackChunkName: "components" */ 'services/PostLoad').then(module => module.default.initialize());
    import(/* webpackChunkName: "components" */ 'services/ThirdParty');
    import(/* webpackChunkName: "components" */ 'services/Basket');
};

export default runApplication;
